"use client";

/**
 * AlertCreatorNode — floating "Made by" byline card rendered on the
 * ReactFlow alert canvas.
 *
 * Unlike `AlertCellNode` this node is DISCONNECTED from the alert flow —
 * no edges in or out. It exists purely to attribute the alert to its
 * curated creator (e.g. Jason Luongo) so visitors understand who built
 * the thing they're looking at before they read the steps.
 *
 *   ┌─ Layout ───────────────────────────────────────────────────┐
 *   │                                                             │
 *   │  MADE BY                                ← transparent bg,   │
 *   │                                           sits on canvas    │
 *   │  ┌─────────────────────────────────┐                        │
 *   │  │ [avatar]  Jason Luongo ✓  ↗     │   ← distinct card bg,  │
 *   │  │           @JasonL_Capital        │     clickable → X      │
 *   │  │           18.4K · Finance. Fit… │                        │
 *   │  └─────────────────────────────────┘                        │
 *   │                                                             │
 *   └─────────────────────────────────────────────────────────────┘
 *
 * Rendering rules:
 *   - Created by AlertCanvas only when `presented_by.kind === "creator"`.
 *     Anonymous fallback payloads do NOT get a node — nothing to show.
 *   - React Flow type: "creatorCard" (registered in AlertCanvas.tsx).
 *   - No <Handle> components → no edges can attach.
 *   - The outer wrapper has transparent bg so the "MADE BY" label floats
 *     on the canvas backdrop; only the inner card has a background.
 *   - Entire card is an <a> → opens creator's X profile in a new tab.
 */

import { memo } from "react";
import { BadgeCheck, ExternalLink } from "lucide-react";
import type { NodeProps } from "@xyflow/react";
import { cn } from "@/lib/utils";
import type { PublicProfile } from "@/lib/api-client";

/** Shape of the `data` object AlertCanvas stuffs onto a creatorCard node. */
export interface AlertCreatorNodeData {
  /** The resolved creator profile. Passing the whole discriminated union
   *  lets us expand later (e.g. anonymous variant) without a data-shape
   *  migration — for now only `kind === "creator"` is rendered. */
  profile: PublicProfile;
  viewMode?: string;
  [key: string]: unknown;
}

const FALLBACK_AVATAR = "/images/avatars/avatar_1.svg";

/** 12400 → "12.4K" · 1_520_000 → "1.5M". Short, human, no fractional Ks
 *  above 10 so "87.3K" reads clean but "12K" doesn't get padded to "12.0K". */
function formatFollowers(n: number): string {
  if (n < 1_000) return `${n}`;
  if (n < 1_000_000) {
    const v = n / 1_000;
    return `${v >= 10 ? Math.round(v) : v.toFixed(1).replace(/\.0$/, "")}K`;
  }
  const v = n / 1_000_000;
  return `${v >= 10 ? Math.round(v) : v.toFixed(1).replace(/\.0$/, "")}M`;
}

function AlertCreatorNodeComponent({ data }: NodeProps) {
  const d = data as unknown as AlertCreatorNodeData;
  const profile = d.profile;
  const viewMode = d.viewMode;

  // Anonymous / missing profiles render nothing — AlertCanvas already
  // guards against adding this node type in those cases, but defence in
  // depth never hurt anyone.
  if (!profile || profile.kind !== "creator") return null;

  const handleHref =
    profile.handle_url ||
    (profile.handle ? `https://x.com/${profile.handle}` : null);

  const followersLabel =
    typeof profile.followers_count === "number" && profile.followers_count > 0
      ? `${formatFollowers(profile.followers_count)} followers`
      : null;

  const CardInner = (
    <>
      <img
        src={profile.avatar_url || FALLBACK_AVATAR}
        alt={profile.display_name}
        className="w-16 h-16 rounded-full object-cover border border-white/10 shrink-0 bg-zinc-900"
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).src = FALLBACK_AVATAR;
        }}
      />

      <div className="flex flex-col min-w-0 leading-tight gap-1">
        {/* Row 1 — display name · ✓ · ↗
            No `ml-auto` on the external-link icon; the whole row should
            hug the text so the card can shrink-wrap to content instead
            of stretching out empty space on the right. */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[18px] font-semibold text-slate-50 truncate max-w-[300px]">
            {profile.display_name}
          </span>
          {profile.verified && (
            <BadgeCheck
              className={`w-[18px] h-[18px] shrink-0 ${viewMode === "signal" ? "text-emerald-300" : "text-sky-300"}`}
              strokeWidth={2.4}
              aria-label="Verified creator"
            />
          )}
          {handleHref && (
            <ExternalLink
              className="w-4 h-4 text-slate-400/80 shrink-0"
              strokeWidth={2.2}
            />
          )}
        </div>

        {/* Row 2 — @handle */}
        {profile.handle && (
          <span className="text-[14px] text-slate-400 font-medium truncate max-w-[300px]">
            @{profile.handle}
          </span>
        )}

        {/* Row 3 — followers · bio */}
        <div className="flex items-center gap-2 mt-1 min-w-0">
          {followersLabel && (
            <span className="text-[13px] text-slate-300/90 tabular-nums font-medium shrink-0">
              {followersLabel}
            </span>
          )}
          {followersLabel && profile.bio && (
            <span className="w-[4px] h-[4px] rounded-full bg-slate-500/70 shrink-0" />
          )}
          {profile.bio && (
            <span className="text-[13px] text-slate-300/85 truncate max-w-[320px]">
              {profile.bio}
            </span>
          )}
        </div>
      </div>
    </>
  );

  // Card surface — distinct bg vs. the canvas backdrop. Neutral slate/zinc
  // so the card reads as elevated but stays in the same palette family as
  // the cell nodes (sky borders / icy accents). Hover nudges the border
  // bright-sky to signal interactivity, same treatment cells get.
  const cardClasses = cn(
    // `w-fit` + `max-w-[520px]` lets the card shrink to content instead
    // of holding a 520px slab with empty space on the right. The max cap
    // is kept so unusually long bios still truncate inside the card
    // rather than blowing the node out horizontally.
    "flex items-center gap-4 w-fit max-w-[520px]",
    "rounded-xl border border-slate-600/50",
    "bg-gradient-to-br from-slate-900/95 via-slate-950/95 to-zinc-950/95",
    "px-5 py-4",
    "shadow-[0_10px_32px_-10px_rgba(0,0,0,0.75)]",
    "transition-colors duration-200",
    "backdrop-blur-sm",
  );

  return (
    // Outer wrapper — NO background. Sits on the canvas with only the
    // "MADE BY" label + card visible. React Flow adds `.react-flow__node`
    // as a positioning wrapper; we kill any default visual styling via
    // inline resets (see `style` below) so nothing paints behind us.
    <div
      className="flex flex-col items-center gap-2"
      style={{
        background: "transparent",
        border: 0,
        boxShadow: "none",
        padding: 0,
      }}
    >
      <span
        className={cn(
          "text-[12px] font-semibold uppercase tracking-[0.26em]",
          viewMode === "signal" ? "text-emerald-200/70 select-none text-center" : "text-sky-200/70 select-none text-center",
        )}
      >
        Made by
      </span>

      {handleHref ? (
        <a
          href={handleHref}
          target="_blank"
          rel="noreferrer noopener"
          className={cn(
            cardClasses,
            viewMode === "signal" ? "hover:border-emerald-300/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60" : "hover:border-sky-300/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60",
          )}
          title={`Open ${profile.display_name}'s profile on X`}
        >
          {CardInner}
        </a>
      ) : (
        <div className={cardClasses}>{CardInner}</div>
      )}
    </div>
  );
}

export const AlertCreatorNode = memo(AlertCreatorNodeComponent);
